import { DataSource } from "@/config/datasources";
import type { DynamicFilterDef } from "@/utils/metadataValueResolver";

const IDENTITY_COLUMN_IDS = [
  "title",
  "legislationExternalId",
  "publishedOn",
] as const;

const STATUS_COLUMN_ID = "isCurrent";

export function buildSourceColumnOrder(
  metadataColumnIds: readonly string[],
  afterStatusColumnIds: readonly string[] = []
): readonly string[] {
  return [
    ...IDENTITY_COLUMN_IDS,
    ...metadataColumnIds,
    STATUS_COLUMN_ID,
    ...afterStatusColumnIds,
  ];
}

export const RETSINFORMATION_COLUMN_ORDER = buildSourceColumnOrder(
  ["documentType", "ministerium"],
  ["consolidationProgress"]
);

export const RETSINFORMATION_DYNAMIC_FILTER_ORDER = [
  "documentType",
  "ministerium",
] as const;

export const ARVID_COLUMN_ORDER = buildSourceColumnOrder([
  "journal_number",
  "authority",
  "type",
  "classification",
]);

export const ARVID_DYNAMIC_FILTER_ORDER = [
  "authority",
  "type",
  "classification",
] as const;

export const DOMSDATABASEN_COLUMN_ORDER = buildSourceColumnOrder([
  "court_case_number",
  "office_name",
  "instance",
  "case_type",
  "profession",
]);

export const DOMSDATABASEN_DYNAMIC_FILTER_ORDER = [
  "office_name",
  "instance",
  "case_type",
  "profession",
] as const;

export const EURLEX_COLUMN_ORDER = buildSourceColumnOrder([
  "CelexNumber",
  "sector",
  "documentType",
]);

export const EURLEX_DYNAMIC_FILTER_ORDER = ["sector", "documentType"] as const;

function buildSourceColumnOrderMap(): Partial<
  Record<DataSource, readonly string[]>
> {
  const map: Partial<Record<DataSource, readonly string[]>> = {
    [DataSource.Retsinformation]: RETSINFORMATION_COLUMN_ORDER,
    [DataSource.Arvid]: ARVID_COLUMN_ORDER,
    [DataSource.Domsdatabasen]: DOMSDATABASEN_COLUMN_ORDER,
    [DataSource.Eurlex]: EURLEX_COLUMN_ORDER,
  };

  return map;
}

function buildSourceDynamicFilterOrderMap(): Partial<
  Record<DataSource, readonly string[]>
> {
  const map: Partial<Record<DataSource, readonly string[]>> = {
    [DataSource.Retsinformation]: RETSINFORMATION_DYNAMIC_FILTER_ORDER,
    [DataSource.Arvid]: ARVID_DYNAMIC_FILTER_ORDER,
    [DataSource.Domsdatabasen]: DOMSDATABASEN_DYNAMIC_FILTER_ORDER,
    [DataSource.Eurlex]: EURLEX_DYNAMIC_FILTER_ORDER,
  };

  return map;
}

export const SOURCE_COLUMN_ORDERS = buildSourceColumnOrderMap();

export const SOURCE_DYNAMIC_FILTER_ORDERS = buildSourceDynamicFilterOrderMap();

export function sortFilterDefsBySourceOrder(
  defs: DynamicFilterDef[],
  order: readonly string[] | undefined
): DynamicFilterDef[] {
  if (!order?.length) return defs;

  const rank = (key: string) => {
    const index = order.indexOf(key);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };

  return [...defs].sort((a, b) => rank(a.key) - rank(b.key));
}

export function getColumnDefId(col: {
  id?: string;
  accessorKey?: string | ((row: unknown) => unknown);
}): string | undefined {
  if (col.id) return col.id;
  if (typeof col.accessorKey === "string") return col.accessorKey;
  return undefined;
}

export function reorderColumnsById<
  T extends {
    id?: string;
    accessorKey?: string | ((row: unknown) => unknown);
  },
>(columns: T[], order: readonly string[]): T[] {
  const byId = new Map<string, T>();
  for (const col of columns) {
    const id = getColumnDefId(col);
    if (id) byId.set(id, col);
  }

  const result: T[] = [];
  const placed = new Set<string>();

  for (const id of order) {
    const col = byId.get(id);
    if (col) {
      result.push(col);
      placed.add(id);
    }
  }

  for (const col of columns) {
    const id = getColumnDefId(col);
    if (!id || !placed.has(id)) {
      result.push(col);
    }
  }

  return result;
}

const DISPLAY_NAME_MAP: Record<string, string> = {
  BEK: "Bekendtgørelse (BEK)",
  Bekendtgørelse: "Bekendtgørelse (BEK)",
  LBK: "Lovbekendtgørelse (LBK)",
  Lovbekendtgørelse: "Lovbekendtgørelse (LBK)",
};

// Capitalize and sort helpers
const capitalize = (str: string) =>
  str.replace(/(^\w|-\w)/g, (match) => match.toUpperCase());

export const processOptions = (values: string[]) => {
  const uniqueValues = Array.from(new Set(values));
  return uniqueValues
    .map((value) => ({
      value,
      label: DISPLAY_NAME_MAP[value] || capitalize(value.replace(/_/g, " ")),
    }))
    .filter(
      (option, index, self) =>
        index === self.findIndex((o) => o.label === option.label)
    )
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const parseSearchTags = (searchTag: string): string[] => {
  const tags: string[] = [];
  let currentTag = "";
  let insideQuotes = false;

  for (let i = 0; i < searchTag.length; i++) {
    const char = searchTag[i];

    if (char === '"') {
      insideQuotes = !insideQuotes;
      currentTag += char; // Keep the quotation mark
    } else if (char === " " && !insideQuotes) {
      if (currentTag.trim()) {
        tags.push(currentTag.trim());
        currentTag = "";
      }
    } else {
      currentTag += char;
    }
  }

  // Add the last tag if there's any remaining
  if (currentTag.trim()) {
    tags.push(currentTag.trim());
  }

  return tags;
};
